import * as React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Button,
    IconButton,
    FormControlLabel,
    Checkbox,
    Link,
    Box,
    Typography,
    Modal,
} from "@mui/material";
import { Visibility, VisibilityOff, Close } from "@mui/icons-material";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import { Formik, Form, Field } from "formik";
import InputAdornment from "@mui/material/InputAdornment";
import { alpha, useTheme } from "@mui/material/styles";
import { MuiTextFieldFormik } from "../../../components/CustomTextField/TextField";
import { validateLoginForm } from "../../../common/utility/validators";
import { useAxios } from "../../../common/api/hooks/useAxios";
import { URL } from "../../../common/api/urls";
import { useNavigate } from "react-router-dom";
import useAuth from "../../../common/hooks/useAuth";
import useRefreshToken from "../../../common/hooks/useRefreshToken";
import {
    TEXT_MSGS,
    REFRESH_POLLING_INTERVAL,
} from "../../../common/utility/constant";
import { getLoginFingerprintPayload } from "../../../common/utility/deviceFingerprint";
import { clearLoginResults } from "../../../common/utility/browserLoginCoordinator";
import { notifyInvalidatedSessions } from "../../../common/api/hooks/useAxiosPrivate";

const initialValues = {
    email_id: "",
    password: "",
    remember_me: true,
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    maxWidth: "calc(100vw - 32px)",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "12px",
    p: "clamp(20px, 5vw, 32px)", // 32px on desktop, tighter on phones
    // Short screens (phones, landscape): scroll inside the modal instead of
    // clipping the top, which is what happened with the centred layout.
    maxHeight: "calc(100dvh - 32px)",
    overflowY: "auto",
    overflowX: "hidden",
};

/* -------------------------------------------------------------------------- */
/*  "Signed in on another device" notice (sessionExpired variant only)        */
/*  Shows the device that logged in and ended this session. Data comes from   */
/*  the refresh API's `last_login` object.                                    */
/* -------------------------------------------------------------------------- */

const MUTED = "#92a9a9";
const MONO_FONT = '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace';

const isEmptyValue = (value) =>
    value === null ||
    value === undefined ||
    ["", "—", "-", "unknown"].includes(String(value).trim().toLowerCase());

/** "Mobile Chrome Mobile browser Android 16" -> phone icon, "desktop ..." -> desktop icon. */
function deviceIcon(device) {
    const kind = String(device ?? "")
        .trim()
        .split(/\s+/)[0]
        .toLowerCase();

    if (kind === "mobile") return SmartphoneOutlinedIcon;
    if (kind === "tablet") return TabletMacOutlinedIcon;
    if (kind === "desktop") return DesktopWindowsOutlinedIcon;
    return DevicesOtherIcon;
}

function LastLoginCell({ label, value, mono = false, full = false }) {
    return (
        <Box sx={{ minWidth: 0, flex: full ? "1 1 100%" : "0 1 auto" }}>
            <Typography sx={{ fontSize: 11, lineHeight: 1.4, color: MUTED }}>
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: mono ? 12 : 13,
                    lineHeight: 1.5,
                    fontFamily: mono ? MONO_FONT : "inherit",
                    fontVariantNumeric: "tabular-nums",
                    color: "text.primary",
                    // IPs and timestamps stay on one line; the cell drops to the next row instead
                    whiteSpace: mono ? "nowrap" : "normal",
                    wordBreak: mono ? "normal" : "break-word",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function LastLoginNotice({ lastLogin }) {
    const theme = useTheme();

    if (!lastLogin) return null;

    const device = lastLogin.device;
    const cells = [
        { label: "Login time", value: lastLogin.login_time, full: true },
        {
            label: "IP address",
            value: lastLogin.ip_addr ?? lastLogin.ip_address ?? lastLogin.ip,
            mono: true,
        },
        { label: "Login location", value: lastLogin.location, mono: true },
    ].filter((cell) => !isEmptyValue(cell.value));

    if (isEmptyValue(device) && cells.length === 0) return null;

    const warning = theme.palette.warning.main;
    const DeviceIcon = deviceIcon(device);

    return (
        <Box
            role="note"
            sx={{
                mt: 2,
                borderRadius: "8px",
                border: `1px solid ${alpha(warning, 0.4)}`,
                bgcolor: alpha(warning, 0.08),
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: 2,
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "10px",
                        bgcolor: alpha(warning, 0.2),
                        border: `1px solid ${alpha(warning, 0.5)}`,
                        color: warning,
                    }}
                >
                    <DeviceIcon sx={{ fontSize: 22 }} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            lineHeight: 1.4,
                            color: warning,
                        }}
                    >
                        Signed in on another device
                    </Typography>
                    {!isEmptyValue(device) && (
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                lineHeight: 1.4,
                                color: "text.primary",
                                wordBreak: "break-word",
                                mt: 0.25,
                            }}
                        >
                            {String(device).trim()}
                        </Typography>
                    )}
                </Box>
            </Box>

            {cells.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        columnGap: "24px",
                        rowGap: "10px",
                        px: 2,
                        py: 1.5,
                        borderTop: `1px solid ${alpha(warning, 0.25)}`,
                    }}
                >
                    {cells.map((cell) => (
                        <LastLoginCell
                            key={cell.label}
                            label={cell.label}
                            value={cell.value}
                            mono={cell.mono}
                            full={cell.full}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

/**
 * One login modal for the whole app.
 *
 * Props
 *  - open:                   boolean
 *  - onClose:                called whenever the modal should close
 *  - variant:                "login" (default) | "sessionExpired"
 *  - msg:                    heading text for "sessionExpired" (defaults to TEXT_MSGS.LOGIN_POPUP_MSG)
 *  - lastLogin:              sessionExpired only. The `last_login` object from the refresh
 *                            response ({ device, location, ip_addr, login_time }). When given,
 *                            a notice shows which device ended this session.
 *  - onLoginSuccess:         (responseData) => void. Replaces the default redirect.
 *  - onInvalidatedSessions:  (sessions[]) => void. Called when the login response
 *                            contains a non-empty `invalidated_sessions` array.
 *
 * "login"          Opened by the user. Has a close button and a sign-up link,
 *                  Esc is disabled, and a successful login goes to "/" unless
 *                  onLoginSuccess is given.
 * "sessionExpired" Opened when the session ends. Stays on the current page after
 *                  login, polls /refresh so another tab's login closes it, and
 *                  Esc clears auth and goes to /landing.
 */

export default function LoginModal(props) {
    const { variant = "login" } = props;
    const isSessionExpired = variant === "sessionExpired";

    const theme = useTheme();
    const { setAuth } = useAuth();
    const refresh = useRefreshToken();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [apiError, setAPIError] = useState(undefined);

    const { responseData, error, loaded, reset, executeAPI } = useAxios();
    const {
        responseData: googleResponseData,
        error: googleError,
        loaded: googleLoaded,
        reset: googleReset,
        executeAPI: googleExecuteAPI,
    } = useAxios();

    const formikRef = useRef();

    // Latest callbacks/props in refs so the effects below don't re-run on every parent render.
    const onCloseRef = useRef(props.onClose);
    const onLoginSuccessRef = useRef(props.onLoginSuccess);
    const onInvalidatedSessionsRef = useRef(props.onInvalidatedSessions);
    const refreshRef = useRef(refresh);
    onCloseRef.current = props.onClose;
    onLoginSuccessRef.current = props.onLoginSuccess;
    onInvalidatedSessionsRef.current = props.onInvalidatedSessions;
    refreshRef.current = refresh;

    const handleClickShowPassword = () => setShowPassword((prev) => !prev);

    // sessionExpired only: if the user logged in from another tab, /refresh succeeds and we close.
    useEffect(() => {
        if (!isSessionExpired || !props.open) return undefined;

        const intervalId = setInterval(async () => {
            try {
                await refreshRef.current();
                onCloseRef.current?.();
            } catch (err) {
                // 410 = session ended for good: stop polling. 401 keeps polling.
                if (err?.response?.status === 410) {
                    clearInterval(intervalId);
                }
            }
        }, REFRESH_POLLING_INTERVAL);

        return () => clearInterval(intervalId);
    }, [isSessionExpired, props.open]);

    // Google login: redirect to Google, or show the error.
    useEffect(() => {
        if (!googleLoaded) return;
        if (googleResponseData != null) {
            window.location.href = googleResponseData.auth_url;
        } else if (googleError !== null) {
            setAPIError(
                googleError?.response?.data?.message ??
                    TEXT_MSGS.NETWORK_ERROR_MSG,
            );
            googleReset();
        }
    }, [googleLoaded, googleResponseData, googleError, googleReset]);

    // Email/password login result.
    useEffect(() => {
        if (!loaded) return;

        if (responseData) {
            setAuth({
                accessToken: responseData?.token,
                name: responseData.first_name + " " + responseData.last_name,
                role: responseData.role,
                resources: responseData.resources,
                email_id: responseData.email_id,
                demo: responseData.demo,
                tos_notify: !!responseData.tos_notify,
                contact_verify_notify: !!responseData.contact_verify_notify,
                handoff: responseData.handoff,
            });

            // Hand the signed-out devices to a parent that stays mounted (this modal is about to close).
            const invalidated = responseData.invalidated_sessions;
            if (Array.isArray(invalidated) && invalidated.length > 0) {
                if (onInvalidatedSessionsRef.current) {
                    onInvalidatedSessionsRef.current(invalidated);
                } else {
                    notifyInvalidatedSessions(invalidated);
                }
            }

            onCloseRef.current?.();

            if (onLoginSuccessRef.current) {
                onLoginSuccessRef.current(responseData);
            } else if (!isSessionExpired) {
                navigate("/", { replace: true });
            }

            // Clear the finished request so this effect doesn't handle the same response twice.
            reset();
        } else if (error) {
            formikRef.current?.setSubmitting(false);
            if (error?.response?.status === 404) {
                formikRef.current?.setErrors({
                    email_id: "Email id not registered",
                });
            } else if (error?.response?.status === 400) {
                formikRef.current?.setErrors({
                    password: "Password is incorrect",
                });
            } else {
                setAPIError(
                    error?.response?.data?.message ??
                        TEXT_MSGS.NETWORK_ERROR_MSG,
                );
            }
            reset();
        }
    }, [
        loaded,
        responseData,
        error,
        isSessionExpired,
        navigate,
        reset,
        setAuth,
    ]);

    const onSubmit = async (values) => {
        setAPIError(undefined);
        values.remember_me = rememberMe;
        clearLoginResults();
        const fingerprint = await getLoginFingerprintPayload();
        executeAPI(
            URL.LOGIN,
            "POST",
            JSON.stringify({ ...values, fingerprint }),
        );
    };

    const onGoogleSubmit = () => {
        setAPIError(undefined);
        googleExecuteAPI(URL.GOOGLE_URL, "GET", {});
    };

    const handleCloseModal = (event, reason) => {
        if (reason === "backdropClick") return;

        onCloseRef.current?.();

        // sessionExpired: Esc means "give up", so sign out and leave the page.
        if (isSessionExpired && reason === "escapeKeyDown") {
            setAuth({});
            navigate("/landing", { replace: true });
        }
    };

    if (!props.open) {
        return null;
    }

    return (
        <Modal
            open={props.open}
            onClose={handleCloseModal}
            aria-labelledby="login-modal-title"
            disableEscapeKeyDown={!isSessionExpired}
        >
            <Box sx={style}>
                {/* Close button: only when the user chose to open the modal */}
                {!isSessionExpired && (
                    <IconButton
                        aria-label="Close"
                        onClick={handleCloseModal}
                        sx={{
                            position: "absolute",
                            right: 16,
                            top: 16,
                            color: theme.palette.grey[500],
                            zIndex: 1,
                        }}
                    >
                        <Close />
                    </IconButton>
                )}

                {isSessionExpired ? (
                    <Typography
                        id="login-modal-title"
                        variant="descriptionLarge"
                        component="h2"
                    >
                        {props.msg ? props.msg : TEXT_MSGS.LOGIN_POPUP_MSG}
                    </Typography>
                ) : (
                    <Typography
                        id="login-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ mb: 2, fontWeight: 600 }}
                    >
                        Log In
                    </Typography>
                )}

                {/* Which device ended this session (from the refresh API's last_login) */}
                {isSessionExpired && (
                    <LastLoginNotice lastLogin={props.lastLogin} />
                )}

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    validate={validateLoginForm}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            {/* Email */}
                            <Box>
                                <Field
                                    component={MuiTextFieldFormik}
                                    fullWidth
                                    required
                                    variant="outlined"
                                    size="small"
                                    color="inputFieldColor"
                                    sx={{ mt: isSessionExpired ? 3 : 0, mb: 3 }}
                                    name="email_id"
                                    type="email"
                                    label="Email"
                                />
                            </Box>

                            {/* Password */}
                            <Box>
                                <Field
                                    component={MuiTextFieldFormik}
                                    fullWidth
                                    required
                                    variant="outlined"
                                    size="small"
                                    color="inputFieldColor"
                                    sx={{ mb: 2 }}
                                    type={showPassword ? "text" : "password"}
                                    label="Password"
                                    name="password"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={
                                                        handleClickShowPassword
                                                    }
                                                >
                                                    {showPassword ? (
                                                        <Visibility />
                                                    ) : (
                                                        <VisibilityOff />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            {/* Remember Me & Forgot Password */}
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 2, flexWrap: "wrap", columnGap: 2 }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={rememberMe}
                                            onChange={() =>
                                                setRememberMe((prev) => !prev)
                                            }
                                            sx={{
                                                color:
                                                    theme.palette.secondaryTheme
                                                        ?.shade03 ||
                                                    theme.palette.primary.main,
                                                "&.Mui-checked": {
                                                    color:
                                                        theme.palette
                                                            .secondaryTheme
                                                            ?.shade03 ||
                                                        theme.palette.primary
                                                            .main,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: "nowrap" }}
                                        >
                                            Remember Me
                                        </Typography>
                                    }
                                />
                                <Link
                                    href="/forgot-password"
                                    variant="body2"
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    Forgot password?
                                </Link>
                            </Box>

                            {/* Submit */}
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={isSubmitting}
                                sx={{
                                    borderRadius: "8px",
                                    py: 1.2,
                                    fontWeight: 600,
                                }}
                            >
                                {isSubmitting ? "Loading..." : "Log In"}
                            </Button>

                            {/* Errors that don't belong to a single field (network, server, Google) */}
                            {apiError && (
                                <Typography
                                    role="alert"
                                    variant="body2"
                                    color="error"
                                    sx={{ mt: 2 }}
                                >
                                    {apiError}
                                </Typography>
                            )}

                            {/* Multi-tab note: only relevant when polling for a login in another tab */}
                            {isSessionExpired && (
                                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                                    Note: If you have multiple tabs open, you
                                    can login only once in any 1 of the tabs and
                                    other tabs should resume automatically.
                                </Typography>
                            )}

                            {/* Register link: only for users who opened the modal themselves */}
                            {!isSessionExpired && (
                                <Box sx={{ mt: 2, textAlign: "center" }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Don't have an account?{" "}
                                        <Link
                                            href="/register"
                                            underline="hover"
                                        >
                                            Sign up
                                        </Link>
                                    </Typography>
                                </Box>
                            )}

                            <br />
                            <br />
                            <hr />
                            <br />
                            <br />

                            <Button
                                variant="outlined"
                                fullWidth
                                sx={{
                                    mb: 2,
                                    backgroundColor: "white",
                                    borderColor: "#dadce0",
                                    color: "#3c4043",
                                    textTransform: "none",
                                    py: 1.2,
                                    "&:hover": {
                                        backgroundColor: "#f8f9fa",
                                        borderColor: "#dadce0",
                                    },
                                }}
                                onClick={onGoogleSubmit}
                                startIcon={
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g fill="none" fillRule="evenodd">
                                            <path
                                                d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7955 2.7164v2.2581h2.9087c1.7023-1.5668 2.6832-3.8741 2.6832-6.615z"
                                                fill="#4285F4"
                                            />
                                            <path
                                                d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.482 18 9 18z"
                                                fill="#34A853"
                                            />
                                            <path
                                                d="M3.9636 10.71c-.18-.54-.2822-1.1173-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.348 2.8268.957 4.0418l3.0063-2.3318z"
                                                fill="#FBBC05"
                                            />
                                            <path
                                                d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.3459l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.482 0 2.4382 2.0168.9573 4.9582L3.9636 7.29C4.6714 5.1627 6.6556 3.5795 9 3.5795z"
                                                fill="#EA4335"
                                            />
                                        </g>
                                    </svg>
                                }
                            >
                                Login with Google
                            </Button>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Modal>
    );
}